import React, { useEffect } from 'react';
import styled from 'styled-components/macro';
import { useGetRootGlossaryNodesQuery, useGetRootGlossaryTermsQuery } from '../../../graphql/glossary.generated';
import { ChildGlossaryTermFragment } from '../../../graphql/glossaryNode.generated';
import { GlossaryNode } from '../../../types.generated';
import { sortGlossaryNodes } from '../../entity/glossaryNode/utils';
import { sortGlossaryTerms } from '../../entity/glossaryTerm/utils';
import { useGlossaryEntityData } from '../../entity/shared/GlossaryEntityContext';
import { useEntityRegistry } from '../../useEntityRegistry';
import { ROOT_NODES, ROOT_TERMS } from '../utils';
import NodeItem from './NodeItem';
import TermItem from './TermItem';
import { ComponentSpinner } from '../../shared/ComponentSpinner';
import { useUserContext } from '../../context/useUserContext';

const BrowserWrapper = styled.div`
    padding: 8px;
    overflow: auto;
    color: #262626;
    font-size: 14px;
    word-break: break-word;
    max-height: 100vh;
    overflow-y: auto;

    &::-webkit-scrollbar {
        height: 12px;
        width: 1px;
        background: #f2f2f2;
    }
    &::-webkit-scrollbar-thumb {
        background: #cccccc;
        -webkit-border-radius: 1ex;
        -webkit-box-shadow: 0 1px 2px rgba(0, 0, 0, 0.75);
    }

    &.f-list-max-height {
        @media (max-width: 992px) {
            max-height: 250px;
        }
    }
`;

interface Props {
    rootNodes?: GlossaryNode[] | any;
    rootTerms?: ChildGlossaryTermFragment[];
    isSelecting?: boolean;
    hideTerms?: boolean;
    openToEntity?: boolean;
    refreshBrowser?: boolean;
    nodeUrnToHide?: string;
    selectTerm?: (urn: string, displayName: string) => void;
    selectNode?: (urn: string, displayName: string) => void;
}

function GlossaryBrowser(props: Props) {
    const {
        rootNodes,
        rootTerms,
        isSelecting,
        hideTerms,
        refreshBrowser,
        openToEntity,
        nodeUrnToHide,
        selectTerm,
        selectNode,
    } = props;

    const user = useUserContext();
    const { urnsToUpdate, setUrnsToUpdate } = useGlossaryEntityData();

    const {
        data: nodesData,
        refetch: refetchNodes,
        loading: nodesLoading,
    } = useGetRootGlossaryNodesQuery({
        skip: !!rootNodes,
        errorPolicy: 'all',
    });
    const {
        data: termsData,
        refetch: refetchTerms,
        loading: termsLoading,
    } = useGetRootGlossaryTermsQuery({
        skip:
            !!rootTerms ||
            (!user?.platformPrivileges?.viewGlossaryTerm && !user?.platformPrivileges?.manageGlossaryTerms),
        errorPolicy: 'all',
    });

    const displayedNodes = rootNodes || nodesData?.getRootGlossaryNodes?.nodes || [];
    const displayedTerms = rootTerms || termsData?.getRootGlossaryTerms?.terms || [];

    const entityRegistry = useEntityRegistry();
    const sortedNodes = displayedNodes.sort((nodeA, nodeB) => sortGlossaryNodes(entityRegistry, nodeA, nodeB));
    const sortedTerms = displayedTerms.sort((termA, termB) => sortGlossaryTerms(entityRegistry, termA, termB));

    useEffect(() => {
        if (refreshBrowser) {
            refetchNodes();
            refetchTerms();
        }
    }, [refreshBrowser, refetchNodes, refetchTerms]);

    // if node(s) or term(s) need to be refreshed at the root level, check if these special cases are in `urnsToUpdate`
    useEffect(() => {
        if (urnsToUpdate.includes(ROOT_NODES)) {
            refetchNodes();
            setUrnsToUpdate(urnsToUpdate.filter((urn) => urn !== ROOT_NODES));
        }
        if (urnsToUpdate.includes(ROOT_TERMS)) {
            refetchTerms();
            setUrnsToUpdate(urnsToUpdate.filter((urn) => urn !== ROOT_TERMS));
        }
    });

    return (
        <>
            {nodesLoading || (termsLoading && <ComponentSpinner />)}
            <BrowserWrapper className="f-list-max-height">
                {sortedNodes.map((node) => (
                    <NodeItem
                        key={node.urn}
                        node={node}
                        isSelecting={isSelecting}
                        hideTerms={hideTerms}
                        openToEntity={openToEntity}
                        refreshBrowser={refreshBrowser}
                        nodeUrnToHide={nodeUrnToHide}
                        selectTerm={selectTerm}
                        selectNode={selectNode}
                    />
                ))}
                {!hideTerms &&
                    sortedTerms.map((term) => (
                        <TermItem key={term.urn} term={term} isSelecting={isSelecting} selectTerm={selectTerm} />
                    ))}
            </BrowserWrapper>
        </>
    );
}

export default GlossaryBrowser;
